/**
 * Browser port of audio_separator's MDX demix loop
 * (architectures/mdx_separator.py :: MDXSeparator.demix / run_model).
 *
 * Model inference itself is injected as `runModel` so this stays independent of
 * the runtime (ONNX Runtime Web in the app, a stub in tests).
 */
import { STFT, SPEC_CHANNELS } from './stft'

/** Params for UVR-MDX-NET-Inst_HQ_3, from mdx_model_data.json + Separator defaults. */
export const MDX = {
  nFft: 6144,
  hop: 1024,
  dimF: 3072,
  segmentSize: 256,
  overlap: 0.25,
  sampleRate: 44100,
} as const

export type RunModel = (spec: Float32Array, frames: number) => Promise<Float32Array>

export interface DemixProgress {
  chunk: number
  totalChunks: number
}

/** A stereo pair. */
export type Stereo = [Float32Array, Float32Array]

export interface Stems {
  /** What the model predicts: the backing track. */
  instrumental: Stereo
  /** The residual, `mix - instrumental`. See `separate()` for why this is exact. */
  vocals: Stereo
}

/**
 * A run of output samples that no future chunk can change, emitted as soon as
 * it settles so it can be played while the rest is still being processed.
 *
 * Note these samples skip the final 0.9 peak normalisation, which needs the
 * whole signal to compute. It only ever scales *down*, and only when the output
 * exceeds 0.9, so streamed playback can differ very slightly in level from the
 * finished file. The arrays returned by `separate()` are normalised properly.
 */
export interface AudioSegment {
  /** Sample offset of this run within the full output. */
  start: number
  inst0: Float32Array
  inst1: Float32Array
  voc0: Float32Array
  voc1: Float32Array
}

export class MdxSeparator {
  private readonly stft: STFT
  readonly chunkSize: number
  readonly trim: number
  private readonly genSize: number

  constructor(private readonly runModel: RunModel) {
    this.stft = new STFT(MDX.nFft, MDX.hop, MDX.dimF)
    this.trim = MDX.nFft >> 1
    this.chunkSize = MDX.hop * (MDX.segmentSize - 1)
    this.genSize = this.chunkSize - 2 * this.trim
  }

  /** Number of model invocations `separate` will make, for progress reporting. */
  chunkCount(length: number): number {
    return Math.ceil(this.paddedLength(length) / this.step())
  }

  private step(): number {
    return Math.trunc((1 - MDX.overlap) * this.chunkSize)
  }

  private paddedLength(length: number): number {
    const pad = this.genSize + this.trim - (length % this.genSize)
    return this.trim + length + pad
  }

  /**
   * Separate a stereo mix into instrumental and vocal stems.
   *
   * The model is an *instrumental* model: one pass predicts the backing track.
   * The vocal stem is the residual, `mix - instrumental`, which costs one
   * subtraction per sample and no extra inference. It is not an approximation
   * of a second model -- for a two-stem split the residual is by definition
   * everything the model did not keep. It does inherit the model's error as
   * instrumental bleed, exactly as upstream's secondary stem does.
   *
   * The subtraction happens in the *normalised* domain, before the gain quirk
   * below is applied, because subtracting a rescaled instrumental from an
   * unscaled mix would not cancel.
   *
   * Gain handling follows MDXSeparator.separate exactly, quirks included:
   * the mix is scaled down to a 0.9 peak (only if it exceeds it), the demixed
   * result is multiplied by the *original* peak, and the output is scaled down
   * to 0.9 (again only if it exceeds it).
   *
   * For a normal full-scale track the middle step is roughly a no-op. For a
   * quiet input it is not -- a mix peaking at 0.03 comes back 30x quieter still.
   * That is upstream behaviour, reproduced here so the port stays faithful.
   */
  async separate(
    ch0: Float32Array,
    ch1: Float32Array,
    onProgress?: (p: DemixProgress) => void,
    onAudio?: (seg: AudioSegment) => void,
  ): Promise<Stems> {
    const n = ch0.length
    const peak = peakOf(ch0, ch1)
    const [in0, in1] = normalizePair(ch0, ch1, 0.9)

    const { trim, chunkSize } = this
    const total = this.paddedLength(n)
    const step = this.step()

    // Padding: `trim` zeros at the front, then enough at the back to land on a
    // whole number of gen_size steps.
    const mix0 = new Float32Array(total)
    const mix1 = new Float32Array(total)
    mix0.set(in0, trim)
    mix1.set(in1, trim)

    const res0 = new Float32Array(total)
    const res1 = new Float32Array(total)
    const divider = new Float32Array(total)

    const part0 = new Float32Array(chunkSize)
    const part1 = new Float32Array(chunkSize)

    const totalChunks = this.chunkCount(n)
    let chunk = 0
    let emitted = 0

    /**
     * Resolve the overlap-add accumulators over [from, to) into final samples,
     * producing both stems. `res/divider` is the instrumental in the normalised
     * domain, so the vocal residual is `mix - res/divider` in that same domain;
     * both are then scaled by `peak` together, which keeps the two stems in a
     * consistent scale (instrumental + vocals == mix * peak).
     */
    const finalize = (from: number, to: number): [Stereo, Stereo] => {
      const len = to - from
      const i0 = new Float32Array(len)
      const i1 = new Float32Array(len)
      const v0 = new Float32Array(len)
      const v1 = new Float32Array(len)
      for (let j = from; j < to; j++) {
        const k = trim + j
        const d = divider[k]
        const o = j - from
        if (d > 1e-8) {
          const a = res0[k] / d
          const b = res1[k] / d
          i0[o] = a * peak
          i1[o] = b * peak
          v0[o] = (mix0[k] - a) * peak
          v1[o] = (mix1[k] - b) * peak
        }
      }
      return [
        [i0, i1],
        [v0, v1],
      ]
    }

    for (let i = 0; i < total; i += step) {
      const start = i
      const end = Math.min(i + chunkSize, total)
      const actual = end - start

      // Zero-pad the tail chunk out to a full chunk_size for the model.
      part0.fill(0)
      part1.fill(0)
      part0.set(mix0.subarray(start, end))
      part1.set(mix1.subarray(start, end))

      const [out0, out1] = await this.runChunk(part0, part1)

      const window = hanningSymmetric(actual)
      for (let j = 0; j < actual; j++) {
        const w = window[j]
        res0[start + j] += out0[j] * w
        res1[start + j] += out1[j] * w
        divider[start + j] += w
      }

      chunk++
      onProgress?.({ chunk, totalChunks })

      // Chunk `chunk` started at (chunk-1)*step and the next one starts at
      // chunk*step, so no future chunk can touch samples below that -- their
      // overlap-add sum and divider are complete and they can be played now.
      if (onAudio) {
        const settled = Math.min(n, chunk * step - trim)
        if (settled > emitted) {
          const [inst, voc] = finalize(emitted, settled)
          onAudio({ start: emitted, inst0: inst[0], inst1: inst[1], voc0: voc[0], voc1: voc[1] })
          emitted = settled
        }
      }
    }

    // Undo the overlap-add weighting, drop the `trim` pad, clip back to length.
    const [inst, voc] = finalize(0, n)
    // Normalised independently: an isolated vocal is much quieter than the mix
    // it came from, and a shared gain would leave it barely audible next to the
    // instrumental when A/B-ing.
    return {
      instrumental: normalizePair(inst[0], inst[1], 0.9),
      vocals: normalizePair(voc[0], voc[1], 0.9),
    }
  }

  /** One model pass: STFT -> zero the lowest bins -> model -> ISTFT. */
  private async runChunk(ch0: Float32Array, ch1: Float32Array): Promise<[Float32Array, Float32Array]> {
    const frames = this.stft.frameCount(ch0.length)
    const spec = this.stft.forward(ch0, ch1)

    // Kill the lowest 3 frequency bins (rumble), as the Python does.
    for (let c = 0; c < SPEC_CHANNELS; c++) {
      const base = c * MDX.dimF * frames
      spec.fill(0, base, base + 3 * frames)
    }

    const pred = await this.runModel(spec, frames)
    return this.stft.inverse(pred, frames)
  }
}

function peakOf(a: Float32Array, b: Float32Array): number {
  let peak = 0
  for (let i = 0; i < a.length; i++) {
    const x = Math.abs(a[i])
    if (x > peak) peak = x
    const y = Math.abs(b[i])
    if (y > peak) peak = y
  }
  return peak
}

/** Scale both channels together so the joint peak is at most `maxPeak`. */
function normalizePair(a: Float32Array, b: Float32Array, maxPeak: number): Stereo {
  const peak = peakOf(a, b)
  if (peak <= maxPeak || peak === 0) return [a, b]

  const g = maxPeak / peak
  const oa = new Float32Array(a.length)
  const ob = new Float32Array(b.length)
  for (let i = 0; i < a.length; i++) {
    oa[i] = a[i] * g
    ob[i] = b[i] * g
  }
  return [oa, ob]
}

/** np.hanning: symmetric, unlike the periodic window used inside the STFT. */
function hanningSymmetric(m: number): Float32Array {
  const w = new Float32Array(m)
  if (m === 1) {
    w[0] = 1
    return w
  }
  for (let i = 0; i < m; i++) w[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (m - 1))
  return w
}
